import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { DatePickerField } from "@/components/ui/DatePicker";
import { toast } from "@/lib/toast";
import {
  createTodo,
  todayIsoDate,
  type TodoDraft,
  type TodoItem,
} from "@/lib/todos";
import { NoteComposer } from "@/projectOverview/notes/NoteComposer";
import type { DraftNoteReference } from "@/projectOverview/notes/types";

/**
 * Kleines Fenster zum schnellen Anlegen eines To-Dos – z. B. aus einer
 * Erwähnung oder um ein Projekt auf Wiedervorlage zu legen. Notiztext mit
 * Erwähnungen (@/#) plus Wiedervorlage-Datum (nur zukünftige Daten).
 */
export function TodoQuickDialog({
  open,
  onClose,
  title,
  description,
  draft,
  initialRemindAt = null,
  requireRemindAt = false,
  successMessage = "To-Do erstellt.",
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Vorbelegter Text + Referenzen (z. B. Projekt-Verknüpfung). */
  draft: TodoDraft;
  initialRemindAt?: string | null;
  /** True = Wiedervorlage-Datum ist Pflicht (Projekt-Wiedervorlage). */
  requireRemindAt?: boolean;
  successMessage?: string;
  onCreated?: (todo: TodoItem) => void;
}) {
  const [remindAt, setRemindAt] = useState<string | null>(initialRemindAt);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bei jedem Öffnen frisch starten.
  useEffect(() => {
    if (open) {
      setRemindAt(initialRemindAt);
      setSaving(false);
      setError(null);
    }
  }, [open, initialRemindAt]);

  const handleSubmit = async (payload: {
    note: string;
    references: DraftNoteReference[];
  }) => {
    if (requireRemindAt && !remindAt) {
      setError("Bitte ein Wiedervorlage-Datum auswählen.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createTodo({
        text: payload.note,
        references: payload.references,
        remind_at: remindAt,
      });
      toast.success(successMessage);
      onCreated?.(created);
      onClose();
    } catch {
      setError("To-Do konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md" title={title} description={description}>
      <div className="space-y-3">
        <NoteComposer
          key={open ? "open" : "closed"}
          orderId={0}
          isBuero={false}
          saving={saving}
          error={error}
          onSubmit={(payload) =>
            void handleSubmit({ note: payload.note, references: payload.references })
          }
          globalReferences
          placeholder="Notiz zum To-Do … (@ Mitarbeiter, # Projekt, + Bestellung, % Material, § Dokument)"
          initialText={draft.text}
          initialReferences={draft.references}
          clearOnSubmit={false}
        />
        <div className="flex flex-wrap items-center gap-2 text-sm text-label-secondary">
          <span className="font-medium text-label">
            Wiedervorlage{requireRemindAt ? " *" : " (optional)"}:
          </span>
          <DatePickerField
            value={remindAt}
            onChange={(value) => {
              setRemindAt(value);
              if (value) setError(null);
            }}
            minDate={todayIsoDate()}
            allowClear={!requireRemindAt}
            placeholder="Datum wählen"
          />
        </div>
        <p className="text-xs text-label-tertiary">
          Speichern über den Senden-Button im Eingabefeld. Das To-Do erscheint in
          „Meine To-Dos &amp; Wiedervorlage“ auf der Startseite
          {requireRemindAt ? " und wird am gewählten Datum prominent markiert." : "."}{" "}
          Mit @ erwähnte Mitarbeiter sehen es ebenfalls, gekennzeichnet als von dir.
        </p>
      </div>
    </Dialog>
  );
}
